import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus, Target, Award, AlertCircle, Info } from 'lucide-react';
import api from '../../services/api';
import { useSocialConnections } from '../../hooks/useSocialConnections';
import ConnectPlaceholder from '../dashboard/ConnectPlaceholder';

interface HealthScoreData {
  success: boolean;
  overall_score: number;
  category: string;
  color: string;
  breakdown: {
    engagement_health: number;
    growth_momentum: number;
    content_consistency: number;
    audience_quality: number;
    platform_optimization: number;
  };
  recommendations: string[];
}

const fetchHealthScore = async (): Promise<HealthScoreData> => {
  console.log('📊 Fetching health score...');
  const response = await api.get('/social/health-score');
  console.log('📊 Health score response:', response.data);
  return response.data;
};

const HealthScoreDashboard: React.FC = () => {
  const { connections, isLoading: connectionsLoading, error: connectionsError, handleConnect } = useSocialConnections();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['healthScore'],
    queryFn: fetchHealthScore,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: connections && connections.length > 0, // Only fetch if there are connections
  });

  const handleRecalculate = async () => {
    try {
      console.log('📊 Recalculating health score...');
      await api.post('/social/health-score/calculate');
      refetch();
    } catch (error) {
      console.error('❌ Error recalculating health score:', error);
    }
  };

  // Show placeholder if no social connections
  if (connectionsLoading) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-64"></div>
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  if (connectionsError) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Unable to Load Connections
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {connectionsError.message}
          </p>
        </div>
      </div>
    );
  }

  if (!connections || connections.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Social Media Health Score</h2>
        </div>
        <ConnectPlaceholder
          message="Connect your social media accounts to see your health score and get personalized recommendations."
          onConnectClick={() => handleConnect('facebook')}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-64"></div>
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
          </div>
          <div className="flex items-center justify-center">
            <div className="w-32 h-32 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Unable to Load Health Score
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 90) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 75) return 'bg-green-50 dark:bg-green-900/10';
    if (score >= 60) return 'bg-yellow-50 dark:bg-yellow-900/10';
    if (score >= 40) return 'bg-orange-50 dark:bg-orange-900/10';
    return 'bg-red-50 dark:bg-red-900/10';
  };

  const getCategoryLabel = (category: string) => {
    const labels = {
      excellent: 'Excellent',
      good: 'Good',
      average: 'Average',
      below_average: 'Below Average',
      poor: 'Needs Improvement'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const componentLabels = {
    engagement_health: 'Engagement Health',
    growth_momentum: 'Growth Momentum',
    content_consistency: 'Content Consistency',
    audience_quality: 'Audience Quality',
    platform_optimization: 'Platform Optimization'
  };

  const maxScores = {
    engagement_health: 30,
    growth_momentum: 25,
    content_consistency: 20,
    audience_quality: 15,
    platform_optimization: 10
  };

  return (
    <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Social Media Health Score</h2>
          <div className="relative group">
            <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <Info className="w-5 h-5" />
            </button>
            <div className="absolute left-0 top-8 w-80 p-4 bg-gray-900 dark:bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <p className="font-semibold mb-2">Health Score Components:</p>
              <ul className="space-y-1.5 text-xs text-gray-200">
                <li>• <strong className="text-white">Engagement (30%):</strong> How well your content performs</li>
                <li>• <strong className="text-white">Growth (25%):</strong> Follower increase over time</li>
                <li>• <strong className="text-white">Consistency (20%):</strong> Regular posting patterns</li>
                <li>• <strong className="text-white">Audience (15%):</strong> Quality of your followers</li>
                <li>• <strong className="text-white">Platform (10%):</strong> Multi-platform presence (1=4pts, 2=7pts, 3+=10pts)</li>
              </ul>
              <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-700">Score updates daily with new metrics</p>
              <div className="absolute -top-2 left-4 w-4 h-4 bg-gray-900 dark:bg-gray-800 border-l border-t border-gray-700 transform rotate-45"></div>
            </div>
          </div>

        </div>
        <button
          onClick={handleRecalculate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
        >
          Recalculate Score
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Overall Score */}
        <div className="flex flex-col items-center lg:col-span-2">
          <div className="relative w-40 h-40 mb-4">
            {/* Background Circle */}
            <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 144 144">
              <circle
                cx="72"
                cy="72"
                r="64"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200 dark:text-slate-700"
              />
              {/* Progress Circle */}
              <circle
                cx="72"
                cy="72"
                r="64"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 64}`}
                strokeDashoffset={`${2 * Math.PI * 64 * (1 - data.overall_score / 100)}`}
                strokeLinecap="round"
                className={`transition-all duration-1000 ease-out ${
                  data.overall_score >= 90 ? 'text-green-500' :
                  data.overall_score >= 75 ? 'text-green-400' :
                  data.overall_score >= 60 ? 'text-yellow-500' :
                  data.overall_score >= 40 ? 'text-orange-500' : 'text-red-500'
                }`}
              />
            </svg>
            {/* Score Text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className={`text-4xl font-bold ${getScoreColor(data.overall_score)}`}>
                  {data.overall_score}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">/ 100</div>
              </div>
            </div>
          </div>
          <div className="text-center">
            <h3 className={`text-xl font-semibold ${getScoreColor(data.overall_score)} mb-2`}>
              {getCategoryLabel(data.category)}
            </h3>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <TrendingUp className="w-4 h-4" />
              <span>Track your progress over time</span>
            </div>
          </div>

          {/* Recommendations below score */}
          {data.recommendations && data.recommendations.length > 0 && (
            <div className="mt-6 w-full max-w-md">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                  Quick Tips
                </h4>
              </div>
              <ul className="space-y-2 text-left">
                {data.recommendations.slice(0, 3).map((recommendation, index) => (
                  <li key={index} className="flex items-start space-x-2 text-xs">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span className="text-slate-800 dark:text-gray-300">
                      {recommendation}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Component Breakdown */}
        <div className="space-y-6 lg:col-span-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Score Breakdown</h3>
          {data.breakdown && Object.entries(data.breakdown).map(([key, value]) => {
            const maxScore = maxScores[key as keyof typeof maxScores];
            const percentage = (value / maxScore) * 100;
            
            return (
              <div key={key} className="flex items-center space-x-4">
                {/* Mini circular progress */}
                <div className="relative w-12 h-12 flex-shrink-0">
                  <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48">
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-gray-200 dark:text-slate-700"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 20}`}
                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - percentage / 100)}`}
                      strokeLinecap="round"
                      className={`transition-all duration-700 ease-out ${
                        percentage >= 80 ? 'text-green-500' :
                        percentage >= 60 ? 'text-yellow-500' :
                        percentage >= 40 ? 'text-orange-500' : 'text-red-500'
                      }`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-semibold !text-black dark:!text-white">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </div>
                
                {/* Label and score */}
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium !text-slate-900 dark:!text-gray-300">
                      {componentLabels[key as keyof typeof componentLabels]}
                    </span>
                    <span className="text-sm font-semibold !text-black dark:!text-white">
                      {value}/{maxScore}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-700 ${
                        percentage >= 80 ? 'bg-green-500' :
                        percentage >= 60 ? 'bg-yellow-500' :
                        percentage >= 40 ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, percentage)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Recommendations */}
      {data.recommendations && data.recommendations.length > 3 && (
        <div className="mt-6 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800">
          <div className="flex items-center space-x-2 mb-3">
            <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-100">
              More Ways to Improve
            </h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.recommendations.slice(3).map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-2 bg-white/50 dark:bg-slate-800/50 p-2.5 rounded-lg">
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-indigo-800 dark:text-indigo-200 text-xs">
                  {recommendation}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HealthScoreDashboard;